<template>
   <section class="login-content">
      <div class="container">
         <div class="row align-items-center justify-content-center height-self-center">
            <div class="col-lg-8">
               <div class="card auth-card">
                  <div class="card-body p-0">
                     <div class="d-flex align-items-center auth-content">
                        <div class="col-lg-7 align-self-center">
                           <div class="p-3">
                              <!-- 로딩 상태 -->
                              <div v-if="isValidating" class="text-center">
                                 <div class="mb-4">
                                    <i class="ri-loader-4-line spin" style="font-size: 3rem; color: #007bff;"></i>
                                 </div>
                                 <h4>링크 확인 중...</h4>
                                 <p>잠시만 기다려주세요.</p>
                              </div>

                              <!-- 유효하지 않은 토큰 -->
                              <div v-else-if="!isValidToken" class="text-center">
                                 <div class="mb-4">
                                    <i class="ri-error-warning-line" style="font-size: 4rem; color: #dc3545;"></i>
                                 </div>
                                 <h4 class="mb-3">유효하지 않은 링크</h4>
                                 <div class="alert alert-danger">
                                    {{ tokenError }}
                                 </div>
                                 <div class="mt-4">
                                    <router-link 
                                       :to="{name: 'auth.forgot-password'}" 
                                       class="btn btn-primary mr-2"
                                    >
                                       새 링크 요청
                                    </router-link>
                                    <router-link 
                                       :to="{name: 'auth.login'}" 
                                       class="btn btn-outline-primary"
                                    >
                                       로그인으로 이동
                                    </router-link>
                                 </div>
                              </div>

                              <!-- 비밀번호 재설정 폼 -->
                              <div v-else-if="!isCompleted">
                                 <h2 class="mb-2">새 비밀번호 설정</h2>
                                 <p v-if="userEmail">{{ userEmail }}의 비밀번호를 재설정합니다.</p>
                                 
                                 <!-- 오류 메시지 -->
                                 <div v-if="errorMessage" class="alert alert-danger" role="alert">
                                    <i class="ri-error-warning-line mr-2"></i>{{ errorMessage }}
                                 </div>

                                 <form @submit.prevent="onSubmit">
                                    <div class="row">
                                       <div class="col-lg-12">
                                          <div class="floating-label form-group">
                                             <input 
                                                class="floating-input form-control" 
                                                type="password" 
                                                placeholder=" " 
                                                v-model="newPassword"
                                                :disabled="isLoading"
                                                required
                                             >
                                             <label>새 비밀번호</label>
                                          </div>
                                          <small class="text-muted">
                                             8-20자리, 영문/숫자/특수문자 포함
                                          </small>
                                       </div>
                                       <div class="col-lg-12 mt-3">
                                          <div class="floating-label form-group">
                                             <input 
                                                class="floating-input form-control" 
                                                type="password" 
                                                placeholder=" " 
                                                v-model="confirmPassword"
                                                :disabled="isLoading"
                                                required
                                             >
                                             <label>비밀번호 확인</label>
                                          </div>
                                       </div>
                                    </div>

                                    <!-- 비밀번호 강도 표시 -->
                                    <div v-if="newPassword" class="mb-3">
                                       <div class="password-strength">
                                          <div class="strength-bar">
                                             <div 
                                                class="strength-fill" 
                                                :class="passwordStrengthClass"
                                                :style="{width: passwordStrengthPercent + '%'}"
                                             ></div>
                                          </div>
                                          <small :class="passwordStrengthClass">
                                             {{ passwordStrengthText }}
                                          </small>
                                       </div>
                                    </div>
                                    
                                    <button 
                                       type="submit" 
                                       class="btn btn-primary btn-block"
                                       :disabled="isLoading || !isPasswordValid"
                                    >
                                       <span v-if="isLoading">
                                          <i class="ri-loader-4-line spin mr-2"></i>처리 중...
                                       </span>
                                       <span v-else>
                                          <i class="ri-lock-password-line mr-2"></i>비밀번호 변경
                                       </span>
                                    </button>
                                 </form>
                              </div>

                              <!-- 완료 상태 -->
                              <div v-else class="text-center">
                                 <div class="mb-4">
                                    <i class="ri-check-double-line" style="font-size: 4rem; color: #28a745;"></i>
                                 </div>
                                 <h4 class="mb-3">비밀번호 변경 완료</h4>
                                 <p class="mb-4">
                                    새로운 비밀번호로 변경되었습니다.<br>
                                    이제 새 비밀번호로 로그인하실 수 있습니다.
                                 </p>
                                 <router-link 
                                    :to="{name: 'auth.login'}" 
                                    class="btn btn-primary"
                                 >
                                    <i class="ri-login-box-line mr-2"></i>로그인하기
                                 </router-link>
                              </div>
                           </div>
                        </div>
                        <div class="col-lg-5 content-right">
                           <img src="@/assets/images/login/01.png" class="img-fluid image-right" alt="">
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   </section>
</template>

<script>
import axios from 'axios'

export default {
   name: 'ResetPassword',
   data() {
      return {
         token: '',
         newPassword: '',
         confirmPassword: '',
         isLoading: false,
         isValidating: true,
         isValidToken: false,
         isCompleted: false,
         userEmail: '',
         errorMessage: '',
         tokenError: ''
      }
   },
   computed: {
      isPasswordValid() {
         const passwordRegex = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,20}$/;
         return passwordRegex.test(this.newPassword) && 
                this.newPassword === this.confirmPassword;
      },

      passwordStrengthPercent() {
         if (!this.newPassword) return 0;
         
         let score = 0;
         if (this.newPassword.length >= 8) score += 25;
         if (/[a-zA-Z]/.test(this.newPassword)) score += 25;
         if (/[0-9]/.test(this.newPassword)) score += 25;
         if (/[!@#$%^&*]/.test(this.newPassword)) score += 25;
         
         return score;
      },

      passwordStrengthClass() {
         const percent = this.passwordStrengthPercent;
         if (percent < 50) return 'text-danger';
         if (percent < 75) return 'text-warning';
         return 'text-success';
      },

      passwordStrengthText() {
         const percent = this.passwordStrengthPercent;
         if (percent < 50) return '약함';
         if (percent < 75) return '보통';
         return '강함';
      }
   },
   async mounted() {
      this.token = this.$route.params.token;
      await this.validateToken();
   },
   methods: {
      async validateToken() {
         try {
            const response = await axios.get(`/api/auth/validate-reset-token/${this.token}`);
            
            if (response.data.valid) {
               this.isValidToken = true;
               this.userEmail = response.data.email;
            } else {
               this.isValidToken = false;
               this.tokenError = response.data.message || '유효하지 않은 토큰입니다.';
            }
         } catch (error) {
            console.error('토큰 검증 오류:', error);
            this.isValidToken = false;
            this.tokenError = '토큰 검증 중 오류가 발생했습니다.';
         } finally {
            this.isValidating = false;
         }
      },

      async onSubmit() {
         if (!this.isPasswordValid) {
            this.errorMessage = '비밀번호를 확인해주세요.';
            return;
         }

         this.isLoading = true;
         this.errorMessage = '';

         try {
            const response = await axios.post('/api/auth/reset-password', {
               token: this.token,
               newPassword: this.newPassword
            });

            if (response.data.success) {
               this.isCompleted = true;
            } else {
               this.errorMessage = response.data.message || '비밀번호 변경에 실패했습니다.';
            }

         } catch (error) {
            console.error('비밀번호 재설정 오류:', error);
            
            if (error.response && error.response.data && error.response.data.message) {
               this.errorMessage = error.response.data.message;
            } else {
               this.errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
            }
         } finally {
            this.isLoading = false;
         }
      }
   }
}
</script>

<style scoped>
.spin {
   animation: spin 1s linear infinite;
}

@keyframes spin {
   from { transform: rotate(0deg); }
   to { transform: rotate(360deg); }
}

.password-strength {
   margin-bottom: 1rem;
}

.strength-bar {
   height: 4px;
   background-color: #e9ecef;
   border-radius: 2px;
   overflow: hidden;
   margin-bottom: 0.25rem;
}

.strength-fill {
   height: 100%;
   transition: all 0.3s ease;
}

.strength-fill.text-danger {
   background-color: #dc3545;
}

.strength-fill.text-warning {
   background-color: #ffc107;
}

.strength-fill.text-success {
   background-color: #28a745;
}

.alert {
   border-radius: 8px;
   border: none;
   padding: 15px;
}

.alert-danger {
   background-color: #f8d7da;
   color: #721c24;
}

.btn:disabled {
   opacity: 0.6;
   cursor: not-allowed;
}

.floating-label {
   position: relative;
}

.floating-input:focus + label,
.floating-input:not(:placeholder-shown) + label {
   transform: translateY(-1.5rem) scale(0.85);
   color: #007bff;
}

.floating-label label {
   position: absolute;
   top: 0.75rem;
   left: 0.75rem;
   transition: all 0.2s ease-in-out;
   pointer-events: none;
   color: #6c757d;
}
</style>
